import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Stack, Paper, Button, TextField, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Grid, Divider,
} from '@mui/material';
import { Download, QrCode2, People, Today, Refresh, Fingerprint, Keyboard, PhoneAndroid } from '@mui/icons-material';
import { gymsAPI, attendanceAPI, authAPI } from '../services/api';
import PaginationBar from '../components/PaginationBar';
import PageHeader from '../components/PageHeader';

import { audioService } from '../utils/audioService';
import { getSocket } from '../services/socket';

function getCurrentUser() {
  try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
}

// gymId may be stored as a plain string or as a populated object { _id: "..." }
function extractGymId(gymId) {
  if (!gymId) return '';
  if (typeof gymId === 'string') return gymId;
  if (typeof gymId === 'object') return gymId._id || gymId.toString();
  return String(gymId);
}

export default function Attendance() {
  const currentUser = getCurrentUser();
  const isSuperadmin = currentUser?.role === 'superadmin';

  // For non-superadmin, gymId comes from their own profile
  const [gyms, setGyms] = useState([]);
  const [selectedGymId, setSelectedGymId] = useState(
    isSuperadmin ? '' : extractGymId(currentUser?.gymId)
  );

  // QR state
  const [qrObjectUrl, setQrObjectUrl] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState('');

  // Attendance state
  const now = new Date();
  const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [attendance, setAttendance] = useState([]);
  const [attLoading, setAttLoading] = useState(false);
  const [attError, setAttError] = useState('');
  const [uniqueMembers, setUniqueMembers] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pages, setPages] = useState(1);

  // Load gyms for superadmin dropdown
  useEffect(() => {
    if (isSuperadmin) {
      gymsAPI.getAll().then(r => {
        const list = r.data.data || [];
        setGyms(list);
        if (list.length > 0 && !selectedGymId) setSelectedGymId('');
      }).catch(() => {});
    } else if (!selectedGymId) {
      // gymId missing from localStorage — fetch fresh profile from server
      authAPI.getMe().then(r => {
        const gymId = extractGymId(r.data?.data?.gymId);
        if (gymId) setSelectedGymId(gymId);
      }).catch(() => {});
    }
  }, [isSuperadmin, selectedGymId]);

  // Load QR when gymId changes
  const loadQr = useCallback(async () => {
    if (!selectedGymId) return;
    setQrLoading(true);
    setQrError('');
    // Revoke previous object URL
    if (qrObjectUrl) URL.revokeObjectURL(qrObjectUrl);
    try {
      const res = await gymsAPI.getQrCode(selectedGymId);
      const url = URL.createObjectURL(res.data);
      setQrObjectUrl(url);
    } catch (e) {
      // Try to parse the error blob for a descriptive message
      let msg = 'Failed to load QR code.';
      try {
        const text = await e.response?.data?.text?.();
        const parsed = text ? JSON.parse(text) : null;
        if (parsed?.message) msg = parsed.message;
      } catch { /* ignore parse errors */ }
      setQrError(msg);
    } finally {
      setQrLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGymId]);

  useEffect(() => {
    loadQr();
    return () => { if (qrObjectUrl) URL.revokeObjectURL(qrObjectUrl); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGymId]);

  // Load attendance
  const loadAttendance = useCallback(async () => {
    if (!isSuperadmin && !selectedGymId) return;
    setAttLoading(true);
    setAttError('');
    try {
      const res = await attendanceAPI.getRecords({ gymId: selectedGymId, startDate, endDate, page, limit });
      setAttendance(res.data.data || []);
      setUniqueMembers(res.data.uniqueMembers || 0);
      setTotalRecords(res.data.total || res.data.count || 0);
      setPages(res.data.pagination?.pages || 1);
    } catch (e) {
      setAttError(e.response?.data?.message || 'Failed to load attendance records');
    } finally {
      setAttLoading(false);
    }
  }, [selectedGymId, startDate, endDate, page, limit, isSuperadmin]);

  useEffect(() => {
    if (isSuperadmin || selectedGymId) loadAttendance();

    // Listen for real-time attendance check-ins over Socket.IO
    const socket = getSocket();
    if (socket) {
      const handleCheckIn = (data) => {
        if (!selectedGymId || String(data.gymId) === String(selectedGymId)) {
          audioService.playAttendanceSuccessSound();
          
          const newRecord = {
            _id: data.attendanceId || `socket_${Date.now()}`,
            userId: { _id: data.userId, name: data.userName },
            gymId: data.gymId,
            checkInTime: data.checkInTime,
            date: data.checkInTime,
            source: data.source || 'qr',
          };

          setAttendance((prev) => [newRecord, ...prev]);
          setTotalRecords((prev) => prev + 1);
        }
      };

      socket.on('attendance:checkin', handleCheckIn);
      return () => socket.off('attendance:checkin', handleCheckIn);
    }
  }, [selectedGymId, page, limit, loadAttendance]);

  const handleDownloadQr = async () => {
    if (!qrObjectUrl) return;
    const a = document.createElement('a');
    a.href = qrObjectUrl;
    a.download = `gym-qr-${selectedGymId}.png`;
    a.click();
  };

  const handleDownloadCsv = () => {
    const header = 'Name,Email,Date,Check-in Time';
    const rows = attendance.map(r => {
      const name = r.userId?.name || '';
      const email = r.userId?.email || '';
      const date = r.date ? new Date(r.date).toLocaleDateString() : '';
      const time = r.checkInTime ? new Date(r.checkInTime).toLocaleTimeString() : '';
      return `"${name}","${email}","${date}","${time}"`;
    });
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-${selectedGymId}-${startDate}-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const selectedGymName = isSuperadmin
    ? gyms.find(g => g._id === selectedGymId)?.name || ''
    : currentUser?.gymName || '';

  return (
    <Box>
      <PageHeader
        title="Attendance & QR Code"
        subtitle="View gym QR code and track member attendance"
        gradient="linear-gradient(90deg, #0891B2, #22D3EE)"
        actions={isSuperadmin && gyms.length > 0 && (
          <TextField
            select
            label="Gym"
            value={selectedGymId}
            onChange={e => { setSelectedGymId(e.target.value); setPage(1); }}
            size="small"
            sx={{ minWidth: 180 }}
            SelectProps={{ native: true }}
          >
            <option value="">All Gyms</option>
            {gyms.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
          </TextField>
        )}
      />

      {!isSuperadmin && !selectedGymId ? (
        <Alert severity="info">Select a gym to view QR code and attendance.</Alert>
      ) : (
        <Grid container spacing={3}>
          {/* ── QR Code Panel ─────────────────────────────────────────── */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, border: 1, borderColor: 'divider', borderRadius: 2, textAlign: 'center', height: '100%' }}>
              {!selectedGymId ? (
                <Alert severity="info" sx={{ textAlign: 'left' }}>Select a specific gym to view its QR code.</Alert>
              ) : (
                <>
                  <Stack alignItems="center" spacing={1} mb={2}>
                    <QrCode2 sx={{ fontSize: 28, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight={700}>Gym QR Code</Typography>
                    {selectedGymName && (
                      <Chip label={selectedGymName} size="small" color="primary" variant="outlined" />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Display at your gym entrance. Members scan to join or check in.
                  </Typography>

                  {qrLoading ? (
                    <Box py={6}><CircularProgress /></Box>
                  ) : qrError ? (
                    <Box>
                      <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{qrError}</Alert>
                      <Button variant="outlined" startIcon={<Refresh />} onClick={loadQr} size="small">
                        Retry
                      </Button>
                    </Box>
                  ) : qrObjectUrl ? (
                    <Box>
                      <Box
                        sx={{
                          display: 'inline-block',
                          p: 2,
                          border: 2,
                          borderColor: 'primary.main',
                          borderRadius: 2,
                          mb: 2,
                          bgcolor: 'white',
                        }}
                      >
                        <img
                          src={qrObjectUrl}
                          alt="Gym QR Code"
                          style={{ width: 220, height: 220, display: 'block' }}
                        />
                      </Box>
                      <Box>
                        <Button
                          variant="contained"
                          startIcon={<Download />}
                          onClick={handleDownloadQr}
                          fullWidth
                        >
                          Download QR Code
                        </Button>
                      </Box>
                    </Box>
                  ) : null}
                </>
              )}
            </Paper>
          </Grid>

          {/* ── Attendance Panel ───────────────────────────────────────── */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
              {/* Summary stats */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={4}>
                  <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 2, textAlign: 'center', border: 1, borderColor: 'primary.100' }}>
                    <People sx={{ color: 'primary.main', mb: 0.5 }} />
                    <Typography variant="h5" fontWeight={800} color="primary.main">{uniqueMembers}</Typography>
                    <Typography variant="caption" color="text.secondary">Unique Members</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 2, textAlign: 'center', border: 1, borderColor: 'success.100' }}>
                    <Today sx={{ color: 'success.main', mb: 0.5 }} />
                    <Typography variant="h5" fontWeight={800} color="success.main">{totalRecords}</Typography>
                    <Typography variant="caption" color="text.secondary">Total Check-ins</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 2, textAlign: 'center', border: 1, borderColor: 'warning.100' }}>
                    <Today sx={{ color: 'warning.main', mb: 0.5 }} />
                    <Typography variant="h5" fontWeight={800} color="warning.main">
                      {attendance.filter(r => {
                        const d = new Date(r.date);
                        const t = new Date();
                        return d.toDateString() === t.toDateString();
                      }).length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Today</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 2 }} />

              {/* Filters */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mb={3} flexWrap="wrap">
                <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    From
                  </Typography>
                  <TextField
                    type="date"
                    size="small"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    sx={{ width: 155 }}
                  />
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    To
                  </Typography>
                  <TextField
                    type="date"
                    size="small"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    sx={{ width: 155 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={loadAttendance}
                    startIcon={<Refresh />}
                    sx={{ height: 40, px: 2.5, borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
                  >
                    Apply
                  </Button>
                </Stack>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Download />}
                  onClick={handleDownloadCsv}
                  disabled={attendance.length === 0}
                  sx={{ height: 40, px: 2, borderRadius: 1.5, textTransform: 'none' }}
                >
                  Export CSV
                </Button>
              </Stack>

              {attError && <Alert severity="error" sx={{ mb: 2 }}>{attError}</Alert>}

              {attLoading ? (
                <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
              ) : attendance.length === 0 ? (
                <Box textAlign="center" py={6}>
                  <People sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No attendance records for the selected period
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Member</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Check-in Time</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Method</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {attendance.map((r, i) => (
                        <TableRow key={i} hover sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Box
                                sx={{
                                  width: 28, height: 28, borderRadius: '50%',
                                  bgcolor: 'primary.main', display: 'flex',
                                  alignItems: 'center', justifyContent: 'center',
                                  fontSize: 12, fontWeight: 700, color: 'white',
                                  flexShrink: 0,
                                }}
                              >
                                {(r.userId?.name || '?')[0].toUpperCase()}
                              </Box>
                              <Typography variant="body2" fontWeight={500}>
                                {r.userId?.name || '—'}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                            {r.userId?.email || '—'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={r.date ? new Date(r.date).toLocaleDateString() : '—'}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {r.checkInTime ? new Date(r.checkInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                          </TableCell>
                          <TableCell>
                            {r.source === 'essl' ? (
                              <Chip icon={<Fingerprint sx={{ fontSize: 14 }} />} label="Biometric" size="small" sx={{ bgcolor: '#65A30D18', color: '#65A30D', fontWeight: 600, fontSize: '0.7rem' }} />
                            ) : r.source === 'manual' ? (
                              <Chip icon={<Keyboard sx={{ fontSize: 14 }} />} label="Manual" size="small" sx={{ bgcolor: 'text.secondary', color: 'text.primary', opacity: 0.8, fontWeight: 600, fontSize: '0.7rem' }} />
                            ) : (
                              <Chip icon={<QrCode2 sx={{ fontSize: 14 }} />} label="QR Code" size="small" sx={{ bgcolor: '#3B82F618', color: '#3B82F6', fontWeight: 600, fontSize: '0.7rem' }} />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {pages > 1 && (
                    <Box mt={2}>
                      <PaginationBar page={page} pages={pages} setPage={setPage} />
                    </Box>
                  )}
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
